"use client";

import * as React from "react";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";

interface BreadcrumbItem {
  label: string;
  href?: string;
  isCurrent?: boolean;
}

interface BreadcrumbProps {
  items: readonly BreadcrumbItem[];
  className?: string;
}

/**
 * Breadcrumb Component
 *
 * Visible breadcrumb navigation that improves UX and SEO.
 * Should match the BreadcrumbList schema in structured data.
 *
 * @example
 * <Breadcrumb
 *   items={[
 *     { label: "דף הבית", href: "/" },
 *     { label: "צור קבלה", href: "/create-receipt", isCurrent: true }
 *   ]}
 * />
 */
function Breadcrumb({ items, className }: BreadcrumbProps) {
  if (!items || items.length === 0) return null;

  return (
    <nav aria-label="ניווט breadcrumb" className={cn("py-3", className)}>
      <ol
        className="flex flex-wrap items-center gap-1 text-sm text-neutral-500"
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        {items.map((item, index) => (
          <li
            key={index}
            className="flex items-center"
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <meta itemProp="position" content={String(index + 1)} />

            {index > 0 && (
              <ChevronLeft
                className="mx-1 h-4 w-4 text-neutral-300 rtl:rotate-180"
                aria-hidden="true"
              />
            )}

            {item.isCurrent || !item.href ? (
              <span
                className="font-medium text-neutral-900"
                itemProp="name"
                aria-current={item.isCurrent ? "page" : undefined}
              >
                {item.label}
              </span>
            ) : (
              <Link
                href={item.href}
                className="transition-colors hover:text-neutral-700"
                itemProp="item"
              >
                <span itemProp="name">{item.label}</span>
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Predefined breadcrumb configurations for common pages
const BREADCRUMB_CONFIGS = {
  home: [{ label: "דף הבית", href: "/", isCurrent: true }],
  createReceipt: [
    { label: "דף הבית", href: "/" },
    { label: "צור קבלה", href: "/create-receipt", isCurrent: true },
  ],
  contact: [
    { label: "דף הבית", href: "/" },
    { label: "צור קשר", href: "/contact", isCurrent: true },
  ],
  legal: [
    { label: "דף הבית", href: "/" },
    { label: "תנאים ופרטיות", href: "/legal", isCurrent: true },
  ],
  changelog: [
    { label: "דף הבית", href: "/" },
    { label: "יומן שינויים", href: "/changelog", isCurrent: true },
  ],
  api: [
    { label: "דף הבית", href: "/" },
    { label: "API", href: "/api", isCurrent: true },
  ],
  shopify: [
    { label: "דף הבית", href: "/" },
    { label: "Shopify", href: "/shopify", isCurrent: true },
  ],

  about: [
    { label: "דף הבית", href: "/" },
    { label: "אודות", href: "/about", isCurrent: true },
  ],
} as const;

export { Breadcrumb, BREADCRUMB_CONFIGS };
export type { BreadcrumbItem, BreadcrumbProps };
